/**
 * Primary label for the patient app top bar (aligned with bottom tabs where possible).
 * @param {string} pathname
 * @returns {string}
 */
export function getPatientShellScreenTitle(pathname) {
  const raw = pathname || "/";
  const p = raw.replace(/\/$/, "") || "/";

  if (p === "/HealBidDashboard") return "Home";
  if (p === "/appointment") return "Appointments";
  if (p === "/appointment-confirmed") return "Appointment";
  if (p === "/patient-requests") return "Requests";
  // No centered title — conversation list/thread carry context; bottom nav labels this tab.
  if (p === "/patient-chats") return "";
  if (p === "/patient-support") return "Help assistant";
  if (p === "/patient-documents") return "Documents";
  if (p === "/patient-notifications") return "Notifications";
  if (p === "/patient-profile/details") return "Your details";
  if (p === "/patient-profile/terms") return "Terms & conditions";
  if (p === "/patient-profile/policy") return "Privacy policy";
  if (p === "/patient-profile/cookies") return "Cookie policy";
  if (p === "/patient-profile/settings") return "Settings";
  if (p === "/patient-profile/app-version") return "App version";
  if (p === "/patient-profile") return "Menu";
  if (p === "/patient-menu") return "Shortcuts";
  if (p === "/request/new") return "New request";

  const stepMatch = raw.match(/^\/request\/[^/]+\/(upload|category|details|review|bidding|bids|appointment)(?:\/|$)/);
  if (stepMatch) {
    const seg = stepMatch[1];
    const bySeg = {
      upload: "Documents",
      category: "Category",
      details: "Details",
      review: "Review",
      bidding: "Doctor replies",
      bids: "Doctor replies",
      appointment: "Choose & book",
    };
    return bySeg[seg] || "Request";
  }
  if (p.startsWith("/request/")) return "Request";

  if (p.startsWith("/patient-match-request/")) return "Request details";
  if (p.startsWith("/care-price")) return "Notify doctors";

  return "HealBid";
}

/**
 * Whether the browser/Router history can pop one entry (tab switches via NavLink push onto the stack).
 */
export function canPatientShellHistoryBack() {
  if (typeof window === "undefined") return false;
  const idx = window.history.state?.idx;
  return typeof idx === "number" && idx > 0;
}

/**
 * How the patient app bar should go “back”.
 * - `none` — home hub and main shell tabs; no back on mobile (spacers), optional brand on desktop.
 * - `path` — navigate to a fixed route (wizard, request detail, etc.).
 * - `history` — `navigate(-1)` like native tabs when stack exists; else `fallbackTo`.
 * @param {string} pathname
 * @returns {{ kind: 'none' } | { kind: 'path', to: string } | { kind: 'history', fallbackTo: string }}
 */
export function getPatientAppBarBackBehavior(pathname) {
  const raw = pathname || "/";
  const p = raw.replace(/\/$/, "") || "/";

  if (p === "/HealBidDashboard") return { kind: "none" };

  /* Full-screen help: offer a clear exit; history when stack exists, else home hub. */
  if (p === "/patient-support") return { kind: "history", fallbackTo: "/HealBidDashboard" };

  // Primary shell tabs (same route as bottom nav): no global back — tabs are the navigation model.
  if (
    p === "/patient-requests" ||
    p === "/patient-chats" ||
    p === "/patient-documents" ||
    p === "/patient-notifications" ||
    p === "/patient-profile" ||
    p === "/patient-menu"
  ) {
    return { kind: "none" };
  }

  if (p.startsWith("/patient-profile/")) return { kind: "path", to: "/patient-profile" };

  if (/\/request\/[^/]+\/(upload|category|details|review)$/.test(raw))
    return { kind: "path", to: "/patient-requests" };
  if (p.startsWith("/request/")) return { kind: "path", to: "/patient-requests" };

  if (p.startsWith("/patient-match-request/")) return { kind: "path", to: "/patient-requests" };
  if (p.startsWith("/care-price")) return { kind: "path", to: "/HealBidDashboard" };
  if (p === "/appointment") return { kind: "path", to: "/HealBidDashboard" };
  if (p === "/appointment-confirmed") return { kind: "path", to: "/appointment" };

  return { kind: "history", fallbackTo: "/HealBidDashboard" };
}
